import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';

import {
  Alert,
  Box,
  Button,
  Field,
  Flex,
  Input,
  Text,
} from '@valwin/facto/components';

import { useErrorTranslator } from '~shared/i18n';
import { usePharmacyId } from '~shared/routes';

import {
  type ResetFormData,
  ResetFormErrors,
  resetSchema,
} from '../model/reset.schema';
import { usePostReset } from '../model/usePostReset';

export function ResetForm() {
  const { t } = useTranslation();
  const pharmacyId = usePharmacyId();

  const { requestReset, reset, isPending, isSuccess, isError } =
    usePostReset(pharmacyId);

  const translateError = useErrorTranslator<ResetFormErrors>({
    EMAIL_REQUIRED: () => t('auth emailInvalidError'),
    EMAIL_INVALID: () => t('auth emailInvalidError'),
  });

  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors, isSubmitting },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit((data) => {
    requestReset(data, { onSuccess: () => resetForm() });
  });

  const handleInputChange = () => {
    if (isError || isSuccess) {
      reset();
    }
  };

  return (
    <Flex flexDirection="column" gap="8" width="full" maxWidth="sm">
      <Text textStyle="sm" color="gray.500" textAlign="center" marginBottom={0}>
        {t('auth resetInstructions')}
      </Text>
      <form onSubmit={onSubmit} noValidate>
        <Box display="flex" flexDirection="column" gap={4}>
          <Field.Root required invalid={!!errors.email?.message}>
            <Field.Label>
              {t('auth email')} <Field.RequiredIndicator />
            </Field.Label>
            <Input
              type="email"
              placeholder={t('auth emailPlaceholder')}
              {...register('email', {
                onChange: handleInputChange,
              })}
            />
            <Field.ErrorText>
              {errors.email?.message && translateError(errors.email.message)}
            </Field.ErrorText>
          </Field.Root>

          {isSuccess && (
            <Alert.Root status="success">
              <Alert.Indicator />
              <Alert.Title>{t('auth resetPasswordEmailSent')}</Alert.Title>
            </Alert.Root>
          )}

          {isError && (
            <Alert.Root status="error">
              <Alert.Indicator />
              <Alert.Title>{t('auth resetPasswordError')}</Alert.Title>
            </Alert.Root>
          )}

          <Flex alignItems="center" marginTop={4}>
            <Button
              type="submit"
              size="sm"
              loading={isPending}
              disabled={isSubmitting || isPending}
              flex={1}
            >
              {t('auth resetPasswordButton')}
            </Button>
          </Flex>
        </Box>
      </form>
    </Flex>
  );
}
